import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const RenderInput = ({ input, value, changeValue }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const baseInputClasses = `
    w-full px-3 py-2.5 text-sm border rounded-lg 
    transition-all duration-200 
    focus:outline-none focus:ring-2 focus:ring-opacity-50
    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
    ${isFocused ? "border-blue-500 ring-blue-500" : "border-gray-300"}
    ${value ? "bg-blue-50/30" : "bg-white"}
    hover:border-gray-400
  `;

  const labelClasses = `
    block text-sm font-medium text-gray-700 mb-1.5
    ${isFocused ? "text-blue-600" : ""}
  `;

  const LabelWithTooltip = () => (
    <div className="flex items-center gap-2 mb-1.5">
      <label className={labelClasses}>
        {input.name}
        {input.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {input.tooltip && (
        <div className="relative">
          <button
            type="button"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
          </button>
          {showTooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg max-w-xs z-10 whitespace-normal">
              {input.tooltip}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const reactSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": { borderColor: "#9ca3af" },
      paddingLeft: 6,
      paddingRight: 6,
      minHeight: 42,
      borderRadius: 8,
      backgroundColor: value ? "rgba(239, 246, 255, 0.3)" : "white",
      fontSize: 14,
    }),
    menu: (provided) => ({ ...provided, zIndex: 30, borderRadius: 8 }),
  };

  switch (input.type) {
    case "searchSelect": {
      const options = input.options || [];
      const isCreatable = input.selectProps?.creatable;

      // kalau value sekarang tidak ada di options, tetap tampilkan sebagai { value, label }
      const selected =
        options.find((o) => o.value === (value ?? "")) ||
        (value ? { value, label: String(value) } : null);

      const commonProps = {
        options,
        isClearable: true,
        isSearchable: true,
        placeholder: input.placeholder || `Search ${input.name}...`,
        value: selected,
        onChange: (opt) => changeValue(input.key, opt ? opt.value : ""),
        onFocus: handleFocus,
        onBlur: handleBlur,
        isDisabled: !!input.disabled,
        styles: reactSelectStyles,
        ...(input.selectProps || {}),
        // saat user ketik manual dan tekan Enter
        onCreateOption: (newValue) => {
          // set langsung nilainya; biarkan pasangan (name/code) diisi hanya kalau ada match
          changeValue(input.key, newValue);
        },
      };

      return (
        <div className="w-full">
          <LabelWithTooltip />
          {isCreatable ? (
            <CreatableSelect {...commonProps} />
          ) : (
            <Select {...commonProps} />
          )}
        </div>
      );
    }

    case "dropdown":
      return (
        <div className="w-full">
          <div className="flex items-center gap-2 mb-1.5">
            <label className={labelClasses}>
              {input.name}
              {input.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {input.tooltip && (
              <div className="relative">
                <button
                  type="button"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onFocus={() => setShowTooltip(true)}
                  onBlur={() => setShowTooltip(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
                </button>
                {showTooltip && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg max-w-ms z-10 whitespace-normal">
                    {input.tooltip}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <select
              className={`${baseInputClasses} appearance-none pr-10 cursor-pointer`}
              value={value || ""}
              onChange={(e) => changeValue(input.key, e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              <option disabled value="" className="text-gray-400">
                Select {input.name}
              </option>
              {input.options?.map((option, index) => (
                <option key={index} value={option.value} className="py-2">
                  {option.name}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`w-4 h-4 transition-transform duration-200 ${
                  isFocused ? "text-blue-500 rotate-180" : "text-gray-400"
                }`}
              />
            </div>
          </div>
        </div>
      );

    case "textarea":
      return (
        <div className="w-full">
          <LabelWithTooltip />
          <textarea
            value={value || ""}
            placeholder={input.placeholder || `Enter ${input.name}`}
            onChange={(e) => changeValue(input.key, e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            rows={input.rows || 3}
            className={`${baseInputClasses} resize-vertical min-h-[80px]`}
          />
        </div>
      );

    case "number":
      return (
        <div className="w-full">
          <LabelWithTooltip />
          <input
            type="number"
            value={value || ""}
            placeholder={input.placeholder || `Enter ${input.name}`}
            onChange={(e) => changeValue(input.key, e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            min={input.min}
            max={input.max}
            step={input.step}
            className={baseInputClasses}
          />
        </div>
      );

    case "text":
    case "email":
    default:
      return (
        <div className="w-full">
          <LabelWithTooltip />
          <input
            type={input.type}
            value={value || ""}
            placeholder={input.placeholder || `Enter ${input.name}`}
            onChange={(e) => changeValue(input.key, e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={baseInputClasses}
          />
        </div>
      );
  }
};

export default RenderInput;
